import * as THREE from "three";
import gsap from "gsap";

import { HeroField } from "./HeroField.js";
import { ProjectCluster } from "./ProjectCluster.js";
import { SCENE_STATES } from "../data/sceneStates.js";

function disposeObject3D(object) {
  object.traverse((child) => {
    if (child.geometry) {
      child.geometry.dispose();
    }

    if (child.material) {
      if (Array.isArray(child.material)) {
        child.material.forEach((material) => material.dispose());
      } else {
        child.material.dispose();
      }
    }
  });
}

export class World {
  constructor({ scene, resources, profile }) {
    this.scene = scene;
    this.resources = resources;
    this.profile = profile;

    this.scene.background = null;

    const environmentMap = this.resources.get("studio-hdr", null);
    if (environmentMap) {
      this.scene.environment = environmentMap;
    }

    this.heroField = new HeroField({ profile });
    this.projectCluster = new ProjectCluster({ resources, profile });

    this.skyDome = this.createSkyDome();
    this.backgroundBillboard = this.createBackgroundBillboard();
    this.floorRing = this.createFloorRing();

    this.heroGroup = new THREE.Group();
    this.heroObject = this.createHeroObject();
    this.heroGroup.add(this.heroObject);

    this.portraitMesh = this.createPortraitMesh();
    this.orbGroup = this.createOrbGroup();

    this.scene.add(this.skyDome);
    this.scene.add(this.backgroundBillboard);
    this.scene.add(this.floorRing);
    this.scene.add(this.heroField.points);
    this.scene.add(this.heroGroup);
    this.scene.add(this.projectCluster.group);
    this.scene.add(this.orbGroup);
    this.scene.add(this.portraitMesh);

    this.addLights();
    this.applyInitialLayout();
  }

  addLights() {
    this.scene.add(new THREE.HemisphereLight("#d8f4ff", "#f5e8d0", 0.85));

    const key = new THREE.DirectionalLight("#ffffff", 1.6);
    key.position.set(4.5, 6, 4);
    this.scene.add(key);

    const cyan = new THREE.PointLight("#48d8ff", 18, 14, 1.5);
    cyan.position.set(-2.5, 1.8, 1.2);
    this.scene.add(cyan);

    const amber = new THREE.PointLight("#ffb85f", 15, 15, 1.9);
    amber.position.set(2.6, -0.4, 2.8);
    this.scene.add(amber);
  }

  applyInitialLayout() {
    this.heroGroup.position.set(0.2, 0.15, -0.8);
    this.projectCluster.group.position.set(1.05, -0.18, -0.1);
    this.projectCluster.group.scale.set(0.92, 0.92, 0.92);
    this.portraitMesh.position.set(-2.15, -0.2, -0.7);
  }

  createSkyDome() {
    const geometry = new THREE.SphereGeometry(16, 48, 48);
    const material = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uColorA: { value: new THREE.Color("#14243f") },
        uColorB: { value: new THREE.Color("#263f67") },
        uColorC: { value: new THREE.Color("#fdbf7a") }
      },
      vertexShader: `
        varying vec3 vWorldPos;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vWorldPos;
        uniform float uTime;
        uniform vec3 uColorA;
        uniform vec3 uColorB;
        uniform vec3 uColorC;

        void main() {
          float h = normalize(vWorldPos).y * 0.5 + 0.5;
          float wave = sin(vWorldPos.x * 0.35 + uTime * 0.12) * 0.08;
          vec3 base = mix(uColorA, uColorB, smoothstep(0.0, 0.85, h + wave));
          vec3 warm = mix(base, uColorC, smoothstep(0.45, 1.0, h) * 0.24);
          gl_FragColor = vec4(warm, 1.0);
        }
      `
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(0, 0.7, -1.5);
    return mesh;
  }

  createBackgroundBillboard() {
    const geometry = new THREE.PlaneGeometry(16, 9, 1, 1);
    const material = new THREE.MeshBasicMaterial({
      map: this.resources.get("background"),
      transparent: true,
      opacity: 0.32,
      blending: THREE.AdditiveBlending
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(0.15, 0.45, -6.4);
    return mesh;
  }

  createFloorRing() {
    const geometry = new THREE.RingGeometry(1.4, 4.6, 80, 1);
    const material = new THREE.MeshStandardMaterial({
      color: "#4cd6ff",
      emissive: "#2ec9ff",
      emissiveIntensity: 0.35,
      metalness: 0.65,
      roughness: 0.2,
      transparent: true,
      opacity: 0.55,
      side: THREE.DoubleSide
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0.2, -1.45, -0.2);
    return mesh;
  }

  createHeroObject() {
    const gltf = this.resources.get("hero-model", null);

    if (gltf && gltf.scene) {
      const model = gltf.scene.clone(true);
      model.scale.setScalar(this.profile.isMobile ? 2.1 : 2.7);
      model.position.set(0, -0.9, 0);
      model.rotation.y = Math.PI * 0.18;
      return model;
    }

    const fallbackGeometry = new THREE.TorusKnotGeometry(0.7, 0.24, 220, 28);
    const fallbackMaterial = new THREE.MeshPhysicalMaterial({
      color: "#7ee2ff",
      emissive: "#35cfff",
      emissiveIntensity: 0.45,
      roughness: 0.16,
      metalness: 0.78,
      clearcoat: 1,
      clearcoatRoughness: 0.12
    });

    const mesh = new THREE.Mesh(fallbackGeometry, fallbackMaterial);
    mesh.position.set(0, -0.45, 0);
    return mesh;
  }

  createPortraitMesh() {
    const frame = new THREE.Group();

    const image = new THREE.Mesh(
      new THREE.PlaneGeometry(1.65, 2.22),
      new THREE.MeshBasicMaterial({
        map: this.resources.get("portrait"),
        transparent: true,
        opacity: 0.98
      })
    );

    const border = new THREE.Mesh(
      new THREE.PlaneGeometry(1.76, 2.33),
      new THREE.MeshBasicMaterial({
        color: "#8adfff",
        transparent: true,
        opacity: 0.38
      })
    );

    border.position.z = -0.02;

    frame.add(border);
    frame.add(image);

    frame.rotation.y = 0.31;
    return frame;
  }

  createOrbGroup() {
    const group = new THREE.Group();
    const orbGeometry = new THREE.IcosahedronGeometry(0.12, 2);

    for (let index = 0; index < 18; index += 1) {
      const hue = 0.52 + Math.random() * 0.18;
      const color = new THREE.Color().setHSL(hue, 0.8, 0.62);
      const material = new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.65,
        metalness: 0.42,
        roughness: 0.25
      });

      const orb = new THREE.Mesh(orbGeometry, material);
      orb.position.set(
        (Math.random() - 0.5) * 8,
        (Math.random() - 0.3) * 4,
        -1.5 - Math.random() * 3.2
      );
      orb.userData.seed = Math.random() * Math.PI * 2;
      orb.userData.speed = 0.35 + Math.random() * 0.45;
      orb.scale.setScalar(0.75 + Math.random() * 1.7);

      group.add(orb);
    }

    return group;
  }

  transitionTo(state, payload = {}) {
    this.heroField.transitionTo(state);
    this.projectCluster.transitionTo(state);

    if (state === SCENE_STATES.PROJECT_DETAIL && payload.slug) {
      this.projectCluster.highlight(payload.slug);
    } else {
      this.projectCluster.clearHighlight();
    }

    const heroTarget = {
      x: 0.2,
      y: 0.15,
      z: -0.8
    };

    if (state === SCENE_STATES.PROJECTS || state === SCENE_STATES.PROJECT_DETAIL) {
      heroTarget.x = -1.25;
      heroTarget.y = 0.02;
      heroTarget.z = -1.2;
    }

    if (state === SCENE_STATES.CONTACT) {
      heroTarget.x = 0.95;
      heroTarget.y = -0.2;
      heroTarget.z = -1.35;
    }

    if (state === SCENE_STATES.LEGAL) {
      heroTarget.x = 1.5;
      heroTarget.y = -0.5;
      heroTarget.z = -2.3;
    }

    gsap.to(this.heroGroup.position, {
      ...heroTarget,
      duration: 0.9,
      ease: "power2.out"
    });

    gsap.to(this.heroGroup.rotation, {
      y: state === SCENE_STATES.PROJECTS || state === SCENE_STATES.PROJECT_DETAIL ? -0.35 : 0,
      duration: 0.9,
      ease: "power2.out"
    });

    gsap.to(this.portraitMesh.position, {
      x: state === SCENE_STATES.LEGAL ? -2.9 : -2.15,
      y: state === SCENE_STATES.CONTACT ? -0.35 : -0.2,
      z: state === SCENE_STATES.LEGAL ? -1.6 : -0.7,
      duration: 0.7,
      ease: "power2.out"
    });

    gsap.to(this.portraitMesh.children[0].material, {
      opacity: state === SCENE_STATES.LEGAL ? 0.16 : 0.38,
      duration: 0.6,
      ease: "power2.out"
    });

    gsap.to(this.portraitMesh.children[1].material, {
      opacity: state === SCENE_STATES.LEGAL ? 0.18 : 0.98,
      duration: 0.6,
      ease: "power2.out"
    });
  }

  update(elapsed) {
    this.heroField.update(elapsed);
    this.projectCluster.update(elapsed);

    const skyMaterial = this.skyDome.material;
    skyMaterial.uniforms.uTime.value = elapsed;

    this.heroGroup.rotation.y += 0.0018;
    this.floorRing.rotation.z = elapsed * 0.09;

    this.orbGroup.children.forEach((orb, index) => {
      const seed = orb.userData.seed;
      const speed = orb.userData.speed;
      orb.position.y += Math.sin(elapsed * speed + seed) * 0.002;
      orb.position.x += Math.cos(elapsed * speed * 0.8 + index) * 0.0012;
      orb.rotation.x += 0.004;
      orb.rotation.y += 0.003;
    });
  }

  dispose() {
    this.heroField.dispose();
    this.projectCluster.dispose();

    this.skyDome.geometry.dispose();
    this.skyDome.material.dispose();

    this.backgroundBillboard.geometry.dispose();
    this.backgroundBillboard.material.dispose();

    this.floorRing.geometry.dispose();
    this.floorRing.material.dispose();

    disposeObject3D(this.heroGroup);
    disposeObject3D(this.portraitMesh);
    disposeObject3D(this.orbGroup);
  }
}
